import { CircleCheck, CircleAlert, X } from 'lucide-react';
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';

type ToastTone = 'success' | 'error';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  notify: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const DURATION_MS = 5000;
let nextId = 1;

/** Transient feedback for completed actions, announced to screen readers. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((all) => all.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, tone: ToastTone = 'success') => {
      const id = nextId++;
      setItems((all) => [...all.slice(-2), { id, tone, message }]);
      window.setTimeout(() => {
        dismiss(id);
      }, DURATION_MS);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-80 flex-col gap-2"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto flex items-start gap-2.5 rounded-lg border border-line-strong bg-surface-raised px-3.5 py-3 text-[13px] text-fg shadow-xl shadow-black/40"
          >
            {t.tone === 'success' ? (
              <CircleCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ok" />
            ) : (
              <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-crit" />
            )}
            <span className="flex-1">{t.message}</span>
            <button
              type="button"
              onClick={() => {
                dismiss(t.id);
              }}
              aria-label="Dispensar notificação"
              className="text-fg-subtle hover:text-fg"
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextValue {
  const ctx = use(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
